// =============================================================================
// coquilleStyles.js — la feuille de la COQUILLE (refonte « Mosaïque vivante »,
// lot L6 « Coquille et transitions », 28/09/2026).
//
// En-tête, rail, sommaire, barre d'onglets, barre de progression de
// navigation, transitions de vue. Assemblée à la fin des RAFFINEMENTS
// (src/feuilleGlobale.js) : après les composants, avant les effets et le SOCLE
// MOBILE, qui reste le dernier bloc et garde la main au doigt (champs à 16 px,
// cibles de 44 pt). Les feuilles « Modules » et « Moi » (FeuilleNavigation.js)
// et la palette (GlobalSearch.js) portent LEUR feuille dans leur morceau
// paresseux, insérée au même endroit par ajouterAuxRaffinements (budget du
// bundle principal).
//
// Règles tenues (cliquet et mouvement, src/cliquet.test.js,
// src/mouvement.test.js) : aucune couleur écrite en dur (jetons, teintes du
// module --cq-h posées par le composant depuis NAV_COLORS), tailles de texte en
// jetons --fs-*, graisses ≤ 600, aucune capitale forcée ; transitions nommées,
// jamais « all » ; aucun survol qui déplace hors (hover:hover) and
// (pointer:fine) ; aucun verre sur un élément fixe ou collant ; View
// Transitions ≤ 200 ms ; mouvement réduit : fondus gardés, déplacements
// retirés (système ET réglage <html data-mouvement="reduit">).
// Surfaces : jetons --surface*/--line/--ink* s'ils existent (lot L4), sinon
// ceux du thème (--bg-card, --border-color, --text-*).
// Commentaires écrits ${/* … */""} : retirés du bundle par la minification.
// =============================================================================

// « Réduire la transparence » (réglage de « Moi » : préfixe html[data-…] ; ou
// réglage du système : préfixe :root sous @media, pour battre le !important
// de primitivesGlobales.css quel que soit l'ordre des feuilles). Réglage
// éteint, RIEN ne bouge : ces règles n'existent pas pour la page (rendu de
// main au pixel près).
//   • plus aucun flou ;
//   • verre (.enio-verre, posé aussi sur les .enio-glass) : la carte opaque
//     passe SOUS ce qu'il porte (background-color seul : un lavis en dégradé
//     reste visible ; un lavis de COULEUR se déclare dans --lavis et prend
//     .enio-lavis, qui le repeint en dégradé par-dessus la carte) ;
//   • voiles (.fen-voile de ModalShell, .enio-voile) : une couche sombre de
//     PLUS, par-dessus leur teinte, donc jamais moins denses qu'avant (0,50
//     devient 0,72) ; le voile clair des feuilles et de la palette passe de
//     55 à 85 %, et un filet détache leurs panneaux (plus de contraste, comme
//     le réglage d'iOS). Tenu par src/materiaux.test.js.
// --scrim et --voile-renfort sont posés ICI, pas dans theme.css (identique à
// main tant que la refonte L10 n'y a pas rangé les jetons de matière).
const reduireTransparence = (p) => `
${p}*{ -webkit-backdrop-filter:none !important; backdrop-filter:none !important; }
${p}body{ --scrim:color-mix(in srgb, var(--bg-app) 85%, transparent); --voile-renfort:rgb(0 0 0 / .44); }
${p}.enio-verre{ background-color:var(--bg-card) !important; }
${p}.enio-lavis{ background-image:linear-gradient(var(--lavis), var(--lavis)) !important; }
${p}:is(.fen-voile, .enio-voile){ background-image:linear-gradient(var(--voile-renfort), var(--voile-renfort)) !important; }
${p}:is(.cq-feuille, .cq-palette){ outline:1px solid var(--cq-ligne); outline-offset:-1px; }`;

export const CSS_CHASSIS = `
${/* ── Jetons de la coquille (sur body : ils dérivent des jetons de thème,
   qui ne prennent leur valeur que sur body.theme-*) ─────────────────────── */""}
body{
  --cq-surface: var(--surface, var(--bg-card));
  --cq-surface-2: var(--surface-2, color-mix(in srgb, var(--text-main) 7%, var(--bg-card)));
  --cq-ligne: var(--line, var(--border-color));
  --cq-encre: var(--ink, var(--text-main));
  --cq-encre-forte: var(--ink-strong, var(--text-main));
  --cq-encre-douce: var(--ink-muted, var(--text-sub));
  --cq-voile: var(--scrim, color-mix(in srgb, var(--bg-app) 55%, transparent));
  --cq-lavis: 8%;
  --cq-lavis-actif: 12%;
  --cq-rail-l: 248px;
}
body.theme-dark{ --cq-lavis: 16%; --cq-lavis-actif: 24%; }

${/* ── En-tête : opaque, 64 px au bureau ────────────────────────────────── */""}
.cq-entete{
  position:sticky; top:0; z-index:9999;
  display:flex; align-items:center; gap:12px; min-height:64px;
  padding:8px 16px; box-sizing:border-box;
  background:var(--bg-app); box-shadow:0 1px 0 var(--cq-ligne);
}
.cq-entete-marque{ flex:none; display:flex; align-items:center; background:none; border:0; padding:0; cursor:pointer; border-radius:var(--radius-sm); }
.cq-entete-marque img{ height:40px; width:auto; object-fit:contain; }
.cq-fil{ flex:1; min-width:0; display:flex; align-items:center; gap:8px; overflow:hidden;
  font-size:var(--fs-meta); font-weight:500; color:var(--cq-encre-douce); white-space:nowrap; }
.cq-fil-racine{ flex:none; }
.cq-fil-sep{ flex:none; color:var(--tx-teal); }
.cq-fil-lien{ flex:0 1 auto; min-width:0; overflow:hidden; text-overflow:ellipsis; background:none; border:0; padding:4px 2px;
  font:inherit; color:inherit; cursor:pointer; border-radius:var(--radius-sm); transition:color var(--dur-fast) ease; }
.cq-fil-courant{ min-width:0; overflow:hidden; text-overflow:ellipsis; font-weight:600; color:var(--cq-encre-forte); }
.cq-entete-titre{ display:none; flex:1; min-width:0; margin:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
  font-size:var(--fs-ligne); font-weight:600; letter-spacing:calc(-.01em * var(--em-ajuste, 1)); color:var(--cq-encre-forte);
  opacity:0; transition:opacity var(--dur-fast) ease; }
${/* Le titre de la page prend le relais du grand titre quand celui-ci passe
   sous l'en-tête (IntersectionObserver, aucun écouteur de défilement). */""}
.cq-entete[data-titre="1"] .cq-entete-titre{ opacity:1; }
.cq-corps{ position:relative; }
.cq-entete-actions{ flex:none; margin-left:auto; display:flex; align-items:center; gap:6px; }
.cq-recherche{ display:inline-flex; align-items:center; gap:10px; height:40px; min-width:248px; padding:0 8px 0 14px;
  border:0; border-radius:var(--radius-pill); background:var(--cq-surface); box-shadow:inset 0 0 0 1px var(--cq-ligne);
  color:var(--cq-encre-douce); font:inherit; font-size:var(--fs-meta); font-weight:500; cursor:pointer;
  transition:transform var(--dur-press) var(--ease-out), box-shadow var(--dur-fast) ease; }
.cq-recherche:active{ transform:scale(var(--motion-press)); }
.cq-kbd{ margin-left:auto; display:inline-flex; gap:3px; }
.cq-entete kbd, .cq-palette kbd{ font-family:inherit; font-size:var(--fs-micro); font-weight:600; line-height:1; min-width:22px; height:22px; padding:0 5px; box-sizing:border-box;
  display:inline-grid; place-items:center; border-radius:6px; background:var(--cq-surface-2); color:var(--cq-encre-douce);
  box-shadow:inset 0 -1px 0 var(--cq-ligne); }
.cq-icone-bouton{ position:relative; flex:none; width:40px; height:40px; display:grid; place-items:center; border:0; padding:0;
  border-radius:var(--radius-md); background:none; color:var(--cq-encre); cursor:pointer;
  transition:transform var(--dur-press) var(--ease-out), background-color var(--dur-fast) ease; }
.cq-icone-bouton:active{ transform:scale(.94); }
${/* Socrate (11/10/2026) : son « S » en Georgia, comme la tête de son panneau,
   à la taille optique des icônes voisines (20 px de colonne : la ligne de
   « Moi » reste alignée). Ouvert, il prend le lavis de l'entrée active du
   rail, en navy (sa teinte) : --cq-surface-2 se confondait avec le fond de
   l'en-tête en thème clair (237,240,241 contre 234,241,245). */""}
/* Synchronisation avec Cowork (super-administrateur, 14/10/2026) : panneau de
   la barre du haut, au bureau et sur iPad en paysage ; au doigt, la page
   Synchronisation (Super administration). */
.cq-synchro-panneau .cq-feuille-corps{ padding:0 10px 16px; }
.cq-synchro-actions{ display:flex; flex-wrap:wrap; gap:8px; padding:12px 6px 4px; }
.cq-socrate-s{ display:inline-block; width:20px; flex:none; text-align:center; line-height:1;
  font-family:Georgia, "Times New Roman", serif; font-size:var(--fs-section); font-weight:700; }
.cq-socrate[aria-expanded="true"]{ color:var(--cq-encre-forte);
  background:color-mix(in srgb, var(--tx-navy) var(--cq-lavis-actif), var(--cq-surface)); }
.cq-compteur{ position:absolute; top:3px; right:2px; min-width:18px; height:18px; padding:0 4px; box-sizing:border-box;
  display:grid; place-items:center; border-radius:5px; background:var(--tuile-navy); color:var(--tuile-navy-glyphe);
  font-size:var(--fs-micro); font-weight:600; line-height:1; box-shadow:0 0 0 2px var(--bg-app); }
.cq-moi-lien .cq-compteur{ position:static; margin-left:auto; box-shadow:none; }
${/* Pastille d'une entrée de menu (lot L11d-bis) : chiffre exact, en ligne à
   droite du libellé (rail, sommaire, tiroir) ; sur l'icône au rail étroit et sur
   la tuile de la feuille « Modules ». */""}
.cq-compteur-nav{ position:static; flex:none; margin-left:auto; border-radius:var(--radius-xs); box-shadow:none; font-variant-numeric:tabular-nums; }
.cq-rail[data-etroit] .cq-compteur-nav{ position:absolute; top:0; right:4px; margin:0; box-shadow:0 0 0 2px var(--cq-surface); }
.cq-module-tuile{ position:relative; }
.cq-module-tuile > .cq-compteur-nav{ position:absolute; top:-6px; right:-8px; margin:0; box-shadow:0 0 0 2px var(--cq-surface); }
.cq-compteur-point{ top:8px; right:8px; min-width:0; width:10px; height:10px; padding:0; border-radius:var(--radius-pill); background:var(--link, var(--tx-teal)); }
.cq-avatar-bouton{ flex:none; margin-left:4px; padding:0; border:0; background:none; cursor:pointer; border-radius:var(--radius-md);
  transition:transform var(--dur-press) var(--ease-out); }
.cq-avatar-bouton:active{ transform:scale(.94); }
.cq-avatar{ width:36px; height:36px; display:inline-grid; place-items:center; border-radius:var(--radius-tile);
  background:var(--tuile-navy); color:var(--tuile-navy-glyphe); font-size:var(--fs-meta); font-weight:600; letter-spacing:calc(.02em * var(--em-ajuste, 1)); flex:none; }
.cq-avatar-grand{ width:52px; height:52px; font-size:var(--fs-ligne); }
.cq-badge-invite, .cq-voir-comme{ display:inline-flex; align-items:center; gap:6px; min-height:32px; padding:4px 10px; border-radius:10px;
  font-size:var(--fs-micro); font-weight:600; white-space:nowrap; }
.cq-badge-invite{ color:var(--tx-teal); box-shadow:inset 0 0 0 1px var(--tx-teal); }
.cq-voir-comme{ border:0; cursor:pointer; color:var(--tx-red); background:color-mix(in srgb, var(--acc-red, currentColor) 14%, transparent); }
.cq-voir-comme-choix{ display:flex; gap:8px; align-items:baseline; width:100%; text-align:left; border:0; background:none; cursor:pointer;
  padding:8px; border-radius:8px; font-size:var(--fs-micro); color:var(--cq-encre); }
.cq-voir-comme-choix span{ color:var(--cq-encre-douce); }
@media (hover:hover) and (pointer:fine){
  .cq-fil-lien:hover{ color:var(--cq-encre-forte); }
  .cq-icone-bouton:hover{ background:var(--cq-surface-2); }
  .cq-recherche:hover{ box-shadow:inset 0 0 0 1px var(--cq-encre-douce); }
  .cq-voir-comme-choix:hover{ background:var(--cq-surface-2); }
}
@media (max-width:1180px){ .cq-recherche{ min-width:0; } .cq-recherche-texte, .cq-kbd{ display:none; } }

${/* Au doigt (barre d'onglets visible : téléphone, iPad en portrait) :
   marque réduite, TITRE de la page à la place du fil, cloche, avatar ; la
   recherche, Socrate et la messagerie passent par les onglets et « Moi ». */""}
html[data-onglets="1"] .cq-entete{ min-height:56px; gap:8px; padding:4px 8px 4px 12px; }
html[data-onglets="1"] .cq-entete-marque img{ height:28px; }
${/* Un seul fil : celui de l'en-tête. Le fil de l'en-tête compact (EnTetePage,
   lot L4) ne sert plus que là où l'en-tête ne montre pas le sien. */""}
html:not([data-onglets="1"]):not([data-coquille="tel"]) .entete-fil{ display:none; }
html[data-onglets="1"] .cq-fil, html[data-onglets="1"] .cq-recherche, html[data-onglets="1"] .cq-messagerie, html[data-onglets="1"] .cq-socrate, html[data-onglets="1"] .cq-synchro, html[data-onglets="1"] .cq-badge-invite span{ display:none; }
html[data-onglets="1"] .cq-entete-titre{ display:block; }
html[data-onglets="1"] .cq-icone-bouton, html[data-onglets="1"] .cq-avatar-bouton{ min-width:44px; min-height:44px; }
html[data-onglets="1"] .cq-avatar-bouton{ display:grid; place-items:center; }

${/* ── Rail (bureau, iPad en paysage) ───────────────────────────────────── */""}
.cq-corps[data-rail]{ display:flex; align-items:stretch; }
.cq-corps[data-rail] > .cq-rail + *{ flex:1 1 auto; min-width:0; }
.cq-rail{
  position:sticky; top:var(--app-header-h, 64px); align-self:flex-start; flex:none;
  width:var(--cq-rail-l); height:calc(var(--vh100) - var(--app-header-h, 64px)); box-sizing:border-box;
  display:flex; flex-direction:column; overflow-y:auto; overscroll-behavior:contain; scrollbar-width:thin;
  padding:14px 12px 12px calc(12px + var(--sal, 0px));
  background:var(--cq-surface); box-shadow:inset -1px 0 0 var(--cq-ligne);
}
html[data-coquille="tablette"] .cq-rail{ --cq-rail-l:232px; }
.cq-rail-liste{ position:relative; display:flex; flex-direction:column; gap:2px; }
.cq-rail-titre{ margin:14px 10px 6px; font-size:var(--fs-micro); font-weight:600; color:var(--cq-encre-douce); }
${/* Filet entre les modules et l'administration (29/09/2026). */""}
.cq-rail-filet{ height:1px; margin:10px 10px 8px; background:var(--cq-ligne); }
.cq-rail-item{
  position:relative; isolation:isolate; display:flex; align-items:center; gap:12px; width:100%; min-height:40px;
  padding:6px 10px 6px 6px; box-sizing:border-box; border:0; border-radius:var(--radius-md); background:none; cursor:pointer;
  font:inherit; font-size:var(--fs-nav); font-weight:500; line-height:1.25; color:var(--cq-encre); text-align:left;
  transition:transform var(--dur-press) var(--ease-out), background-color var(--dur-fast) ease;
}
.cq-rail-item:active{ transform:scale(var(--motion-press)); }
.cq-rail-item[aria-current]{ font-weight:600; color:var(--cq-encre-forte); }
.cq-fond{ position:absolute; inset:0; z-index:-1; border-radius:inherit; pointer-events:none;
  background:color-mix(in srgb, var(--cq-h) var(--cq-lavis-actif), var(--cq-surface)); }
.cq-rail-tuile{ display:inline-grid; flex:none; border-radius:var(--radius-tile); }
.cq-rail-texte{ flex:1; min-width:0; overflow-wrap:anywhere; }
.cq-rail-moi{ display:flex; align-items:center; gap:10px; width:100%; margin-top:auto; padding:14px 8px 6px; box-sizing:border-box;
  border:0; border-top:1px solid var(--cq-ligne); background:none; cursor:pointer; text-align:left; font:inherit;
  border-radius:0 0 var(--radius-md) var(--radius-md); transition:transform var(--dur-press) var(--ease-out); }
.cq-rail-moi:active{ transform:scale(.99); }
.cq-rail-moi[aria-current="page"]{ background:var(--cq-surface-2); }
.cq-rail-moi-texte{ display:flex; flex-direction:column; min-width:0; }
.cq-rail-moi-nom{ font-size:var(--fs-meta); font-weight:600; color:var(--cq-encre-forte); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.cq-rail-moi-role{ font-size:var(--fs-micro); font-weight:500; color:var(--cq-encre-douce); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
${/* Chevron de la ligne de module : replie ou rouvre son sommaire sans
   changer de page (180 ms). Posé SUR la ligne, dont le lien garde toute la
   largeur (lavis et pastille compris) ; 44 px au doigt. */""}
.cq-rail-ligne{ position:relative; }
.cq-rail-ligne[data-pages] > .cq-rail-item{ padding-right:42px; }
.cq-rail-deplier{ position:absolute; top:50%; right:2px; width:36px; height:36px; margin-top:-18px; display:grid; place-items:center;
  padding:0; border:0; border-radius:10px; background:none; color:var(--cq-encre-douce); cursor:pointer; }
.cq-rail-deplier:focus-visible{ outline-offset:-2px; }
.cq-rail-deplier svg{ transition:transform var(--dur-popover) var(--ease-out); }
.cq-rail-deplier[aria-expanded="true"] svg{ transform:rotate(90deg); }
${/* Sommaire à deux niveaux, JUSTE SOUS son module (accordéon, 220 ms). Le
   filet vertical descend de la tuile du module (centre à 20 px). */""}
.cq-sommaire{ display:grid; grid-template-rows:0fr; transition:grid-template-rows 220ms var(--ease-out); }
.cq-sommaire[data-ouvert="1"]{ grid-template-rows:1fr; }
.cq-sommaire-in{ min-height:0; overflow:hidden; }
.cq-sommaire-liste{ position:relative; margin:2px 0 8px 16px; opacity:0; transition:opacity 160ms ease; }
.cq-sommaire[data-ouvert="1"] .cq-sommaire-liste{ opacity:1; }
.cq-sommaire-liste::before{ content:""; position:absolute; left:4px; top:4px; bottom:4px; width:1px; background:var(--cq-ligne); }
.cq-rail[data-instantane] :is(.cq-sommaire, .cq-sommaire-liste, .cq-rail-deplier svg){ transition:none; }
.cq-sommaire-groupe{ margin:10px 10px 4px 16px; font-size:var(--fs-micro); font-weight:600; color:var(--cq-encre-douce); }
.cq-sommaire-ligne{ display:flex; align-items:center; gap:2px; }
.cq-sommaire-ligne[data-niveau="2"]{ padding-left:18px; }
.cq-sommaire-item{ position:relative; flex:1; min-width:0; display:flex; align-items:center; gap:10px; min-height:36px;
  padding:4px 10px 4px 12px; box-sizing:border-box; border:0; border-radius:10px; background:none; cursor:pointer; text-align:left;
  font:inherit; font-size:var(--fs-nav); font-weight:500; color:var(--cq-encre-douce);
  transition:color var(--dur-fast) ease, background-color var(--dur-fast) ease; }
.cq-sommaire-ligne[data-niveau="2"] .cq-sommaire-item{ font-size:var(--fs-meta); }
.cq-sommaire-item[aria-current="page"]{ color:var(--cq-encre-forte); font-weight:600; }
${/* break-word et non anywhere : on passe à la ligne ENTRE deux mots. Un mot
   plus large que la colonne (« d'administration », 119 px, dans les 94 px du
   sommaire sur iPad en paysage) se coupe à une SYLLABE, avec son trait
   d'union (césure du français, lang="fr"), jamais n'importe où ; les mots
   courts ne sont jamais coupés. */""}
.cq-sommaire-texte{ flex:1; min-width:0; overflow-wrap:break-word; -webkit-hyphens:auto; hyphens:auto; hyphenate-limit-chars:10 4 4; -webkit-hyphenate-limit-before:4; -webkit-hyphenate-limit-after:4; -webkit-hyphenate-limit-lines:1; }
.cq-filet{ position:absolute; left:3px; top:6px; bottom:6px; width:2px; border-radius:2px; background:var(--cq-h); }
.cq-sommaire-deplier{ flex:none; width:28px; height:28px; display:grid; place-items:center; border:0; border-radius:8px;
  background:none; color:var(--cq-encre-douce); cursor:pointer; }
.cq-sommaire-deplier svg{ transition:transform var(--dur-fast) var(--ease-out); }
.cq-sommaire-deplier[aria-expanded="true"] svg{ transform:rotate(90deg); }
@media (hover:hover) and (pointer:fine){
  .cq-rail-item:not([aria-current]):hover, .cq-rail-ligne:hover > .cq-rail-item:not([aria-current]), .cq-sommaire-item:not([aria-current]):hover{ background:color-mix(in srgb, var(--cq-h) var(--cq-lavis), transparent); }
  .cq-sommaire-item:hover{ color:var(--cq-encre-forte); }
  .cq-rail-moi:hover{ background:color-mix(in srgb, var(--cq-surface-2) 60%, transparent); }
  .cq-sommaire-deplier:hover{ background:var(--cq-surface-2); }
  .cq-rail-deplier:hover{ color:var(--cq-encre-forte); background:color-mix(in srgb, var(--cq-h) var(--cq-lavis), transparent); }
}
@media (pointer:coarse){
  .cq-rail-item, .cq-sommaire-item{ min-height:44px; }
  .cq-sommaire-deplier{ width:44px; height:44px; }
  .cq-rail-deplier{ right:0; width:44px; height:44px; margin-top:-22px; }
  .cq-rail-ligne[data-pages] > .cq-rail-item{ padding-right:46px; }
}
${/* Bureau étroit (moins de 1 024 px) ou fiche ouverte : icônes seules, bulle
   au survol ; les pages du module passent par le bouton « Sommaire ». */""}
.cq-rail[data-etroit]{ --cq-rail-l:76px; padding:14px 10px 12px calc(10px + var(--sal, 0px)); }
.cq-rail[data-etroit] .cq-rail-texte, .cq-rail[data-etroit] .cq-rail-titre, .cq-rail[data-etroit] .cq-rail-moi-texte{ display:none; }
.cq-rail[data-etroit] .cq-rail-item{ justify-content:center; padding:6px 0; }
.cq-rail[data-etroit] .cq-rail-moi{ justify-content:center; padding:12px 0 4px; }
@media (hover:hover) and (pointer:fine){
  .cq-rail[data-etroit]{ overflow:visible; }
  .cq-rail[data-etroit] .cq-rail-item::after, .cq-rail[data-etroit] .cq-rail-moi::after{
    content:attr(data-bulle); position:absolute; left:calc(100% + 10px); top:50%; z-index:9;
    padding:6px 10px; border-radius:8px; background:var(--cq-encre-forte); color:var(--bg-app);
    font-size:var(--fs-micro); font-weight:600; white-space:nowrap; pointer-events:none;
    opacity:0; visibility:hidden; transform:translateY(-50%);
    transition:opacity 120ms ease, visibility 0s linear 120ms; }
  .cq-rail[data-etroit] .cq-rail-item:hover::after, .cq-rail[data-etroit] .cq-rail-moi:hover::after{
    opacity:1; visibility:visible; transition:opacity 120ms ease 200ms, visibility 0s linear 200ms; }
  .cq-rail[data-etroit] .cq-rail-moi{ position:relative; }
  ${/* Rail étroit plus haut que la fenêtre (data-deborde, RailNavigation.js :
     dix modules en « Grand » ou « Très grand », fenêtre basse) : il défile,
     pour que ses dernières lignes (Administration, Moi) restent atteignables ;
     la bulle, qui serait rognée et ferait défiler le rail en largeur, se tait
     (l'infobulle du navigateur, title, la remplace). */""}
  .cq-rail[data-etroit][data-deborde]{ overflow-y:auto; }
  .cq-rail[data-etroit][data-deborde] .cq-rail-item::after, .cq-rail[data-etroit][data-deborde] .cq-rail-moi::after{ content:none; }
  ${/* iPad sous la taille du texte (data-texte-ajuste="jetons", theme.css) : le
     texte d'un pseudo-élément qui déclare sa taille n'est pas ramené par
     WebKit, le zoom le double ; la bulle repart de son jeton divisé. */""}
  html[data-texte-ajuste="jetons"] .cq-rail[data-etroit] .cq-rail-item::after, html[data-texte-ajuste="jetons"] .cq-rail[data-etroit] .cq-rail-moi::after{
    font-size:calc(var(--fs-micro) / var(--fs-zoom)); }
}

${/* ── Barre d'onglets (téléphone, iPad en portrait) : opaque ───────────── */""}
${/* Lot L11r7 : libellés au plancher de 12 px (--fs-micro), JAMAIS tronqués
   (plus d'ellipse), dans les trois tailles de texte, de 320 à 440 px et en
   iPad portrait. Un onglet ne descend ni sous la largeur de son libellé
   (min-width:max-content) ni sous sa pastille (52 px) ; 3 px de part et
   d'autre du libellé le détachent de ses voisins. Les onglets se partagent la
   barre à parts égales tant que tout tient. Sinon BarreOnglets.js
   (ajusterBarre) descend des PALIERS, un à un, jusqu'au premier où tout tient,
   puis retire ceux qu'un plus fort a rendus inutiles :
     data-court        « Rechercher » s'écrit « Chercher » (le nom accessible
                       reste entier) ;
     data-deux-lignes  le libellé du module peut passer sur deux lignes
                       (« Ressources / humaines ») ; la barre grandit alors de
                       12 px, et seulement si le libellé prend VRAIMENT deux
                       lignes (html[data-onglets-lignes="2"]) ;
     data-serre        1 px de part et d'autre (un mot seul : Gouvernance,
                       Administration) ;
     data-echelle      la barre revient à l'échelle de la taille normale.
   Mesures (Montserrat 12/600) : Accueil 45, Modules 53, Rechercher 71,
   Chercher 57, Moi 23, Gouvernance 83, Nos Mutuelles 88, Ressources humaines
   135, Administration 93 ; banc de la recette L11r7. */""}
.cq-onglets{
  position:fixed; left:0; right:0; bottom:0; z-index:950;
  height:var(--h-onglets); box-sizing:border-box;
  padding:0 calc(2px + var(--sar, 0px)) var(--sab, 0px) calc(2px + var(--sal, 0px));
  display:flex;
  background:var(--cq-surface); box-shadow:0 -1px 0 var(--cq-ligne);
}
.cq-onglet{ position:relative; display:flex; flex-direction:column; align-items:center; gap:4px; flex:1 1 0; min-width:max-content; min-height:44px;
  padding:5px 0 0; border:0; background:none; cursor:pointer; -webkit-tap-highlight-color:transparent;
  font:inherit; font-size:var(--fs-micro); font-weight:600; line-height:1.2; color:var(--cq-encre-douce); }
.cq-onglet[aria-current="page"]{ color:var(--cq-encre-forte); }
.cq-onglet-pastille{ position:absolute; top:5px; left:50%; width:52px; height:32px; margin-left:-26px; border-radius:12px;
  background:color-mix(in srgb, var(--cq-h, var(--tuile-navy)) 16%, var(--cq-surface)); pointer-events:none; }
body.theme-dark .cq-onglet-pastille{ background:color-mix(in srgb, var(--cq-h, var(--tuile-navy)) 30%, var(--cq-surface)); }
.cq-onglet:not([style]) .cq-onglet-pastille{ background:var(--cq-surface-2); }
.cq-onglet-glyphe{ position:relative; z-index:1; display:grid; place-items:center; width:52px; height:32px;
  transition:transform var(--dur-press) var(--ease-out); }
.cq-onglet[aria-current="page"] .cq-onglet-glyphe{ color:var(--cq-hi, var(--cq-encre-forte)); }
.cq-onglet:active .cq-onglet-glyphe{ transform:scale(.9); }
.cq-onglet-libelle{ position:relative; z-index:1; padding:0 3px; white-space:nowrap; text-align:center; }
.cq-libelle-court, .cq-onglets[data-court] .cq-libelle-long{ display:none; }
.cq-onglets[data-court] .cq-libelle-court{ display:inline; }
.cq-onglets[data-deux-lignes] .cq-onglet[data-onglet="module"]{ min-width:min-content; }
.cq-onglets[data-deux-lignes] .cq-onglet[data-onglet="module"] .cq-onglet-libelle{ white-space:normal; line-height:1.1; }
.cq-onglets[data-serre] .cq-onglet-libelle{ padding:0 1px; }
html[data-onglets="1"][data-onglets-lignes="2"]{ --h-onglets:calc(70px + var(--sab,0px)); --bas-flottant:calc(80px + var(--sab,0px)); }
${/* Très grand (zoom 1,38 : 272 px utiles à 375 px, où « Gouvernance » et
   « Rechercher » ne tiennent plus côte à côte) : la barre s'arrête à
   l'échelle de « Grand » (1,38 x 0,855 = 1,18 ; les zooms s'imbriquent),
   comme la barre d'onglets d'iOS, qui ne suit pas la taille du texte. Sa
   hauteur RENDUE reste celle que la page réserve (--h-onglets à l'échelle du
   texte) : rien ne bouge au-dessus d'elle. Palier « échelle » : 1. */""}
body.fs-xl .cq-onglets{ zoom:.855; height:calc(var(--h-onglets) / .855); }
body.fs-lg .cq-onglets[data-echelle]{ zoom:.847; height:calc(var(--h-onglets) / .847); }
body.fs-xl .cq-onglets[data-echelle]{ zoom:.725; height:calc(var(--h-onglets) / .725); }
${/* Safari iOS et iPadOS (data-texte-ajuste, src/echelleTexte.js) : le texte
   n'y suit pas le zoom imbriqué. Chaque zoom de la barre reçoit l'échelle du
   texte x le même facteur : en pourcentage (iPhone), en jetons (iPad, seul
   --fs-micro est lu par la barre). */""}
html[data-texte-ajuste="pourcentage"] body.fs-xl .cq-onglets{ -webkit-text-size-adjust:calc(var(--fs-zoom) * 85.5%); text-size-adjust:calc(var(--fs-zoom) * 85.5%); }
html[data-texte-ajuste="pourcentage"] body.fs-lg .cq-onglets[data-echelle]{ -webkit-text-size-adjust:calc(var(--fs-zoom) * 84.7%); text-size-adjust:calc(var(--fs-zoom) * 84.7%); }
html[data-texte-ajuste="pourcentage"] body.fs-xl .cq-onglets[data-echelle]{ -webkit-text-size-adjust:calc(var(--fs-zoom) * 72.5%); text-size-adjust:calc(var(--fs-zoom) * 72.5%); }
html[data-texte-ajuste="jetons"] body.fs-xl .cq-onglets{ --fs-texte:calc(var(--fs-zoom) * .855); --fs-micro:calc(var(--text-xs) * var(--fs-texte)); }
html[data-texte-ajuste="jetons"] body.fs-lg .cq-onglets[data-echelle]{ --fs-texte:calc(var(--fs-zoom) * .847); --fs-micro:calc(var(--text-xs) * var(--fs-texte)); }
html[data-texte-ajuste="jetons"] body.fs-xl .cq-onglets[data-echelle]{ --fs-texte:calc(var(--fs-zoom) * .725); --fs-micro:calc(var(--text-xs) * var(--fs-texte)); }
.cq-onglet-compteur{ position:absolute; top:1px; right:8px; min-width:16px; height:16px; padding:0 4px; box-sizing:border-box;
  border-radius:5px; background:var(--tuile-navy); color:var(--tuile-navy-glyphe); font-size:var(--fs-micro); font-weight:600;
  line-height:16px; text-align:center; box-shadow:0 0 0 2px var(--cq-surface); }

${/* ── Barre de progression de navigation : 2 px, teinte du module visé ─── */""}
.cq-progression{ position:fixed; left:0; right:0; top:var(--app-header-h, 56px); z-index:9998; height:2px; pointer-events:none;
  background:var(--cq-h, var(--tx-teal)); transform-origin:0 50%; transform:scaleX(0); opacity:0; }
.cq-progression[data-etat="charge"]{ opacity:1; transform:scaleX(.85);
  transition:transform 2400ms var(--ease-out-soft) 100ms, opacity 120ms ease 100ms; }
.cq-progression[data-etat="termine"]{ opacity:0; transform:scaleX(1);
  transition:transform 160ms var(--ease-out), opacity 200ms ease 120ms; }

${/* ── Transitions de vue (M3) : racine en fondu enchaîné à courbes ÉGALES
   (un pixel identique avant et après ne bouge pas : l'en-tête, le rail et
   les onglets ne clignotent pas) ; la tuile du module rejoint celle de
   l'en-tête de module en 200 ms. Plafond de 200 ms (mouvement.test.js).
   Le GROUPE racine aussi : laissé à sa durée par défaut (250 ms), il gardait
   le calque, donc la page aveugle aux clics, 90 ms après la fin du fondu. */""}
::view-transition-group(root), ::view-transition-old(root), ::view-transition-new(root){ animation-duration:160ms; animation-timing-function:ease; }
::view-transition-group(aramis-tuile){ animation-duration:200ms; animation-timing-function:var(--ease-in-out); }
::view-transition-old(aramis-tuile), ::view-transition-new(aramis-tuile){ animation-duration:200ms; }

${/* ── Réduire la transparence : voir reduireTransparence() en tête ─────── */""}
${reduireTransparence('html[data-transparence="reduite"] ')}
@media (prefers-reduced-transparency: reduce){${reduireTransparence(":root ")}
}

${/* ── Mouvement réduit : fondus gardés, déplacements retirés ───────────── */""}
@media (prefers-reduced-motion: reduce){
  .cq-sommaire, .cq-sommaire-liste, .cq-sommaire-deplier svg, .cq-rail-deplier svg{ transition:none; }
  .cq-progression[data-etat]{ transform:scaleX(1); transition:opacity 120ms ease; }
  ::view-transition-group(*), ::view-transition-old(*), ::view-transition-new(*){ animation:none !important; }
}
:root[data-mouvement="reduit"] .cq-sommaire, :root[data-mouvement="reduit"] .cq-sommaire-liste,
:root[data-mouvement="reduit"] .cq-sommaire-deplier svg, :root[data-mouvement="reduit"] .cq-rail-deplier svg{ transition:none; }
:root[data-mouvement="reduit"] .cq-progression[data-etat]{ transform:scaleX(1); transition:opacity 120ms ease; }
`;
